/**
 * AG Grid Cell Renderers for PACS Table
 * These renderers match the existing Ant Design table styling
 */

import React from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { Tag, Tooltip, Button, Dropdown, Menu, Select, message } from 'antd';
import { RightCircleOutlined } from '@ant-design/icons';
import { hasPermission, getMlcTooltip, getUserDetails } from '../../utils/helper';
import { StudyDateTime } from '../StudyDateTime';
import { Permissions, getPacsStatusLabel } from '../../constants/fieldLabels';
import { ReReportedBadge } from '../ReReportedBadge';

/**
 * Two-line clamp for text cells — wrap once, then ellipsis, instead of the old
 * single-line truncation. Shared by the simple text cell renderers below.
 */
const TEXT_CLAMP_2: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  width: '100%',
  lineHeight: 1.2,
};

/**
 * Status tag color mapping
 */
const getStatusStyle = (status: string) => {
  const baseStyle: React.CSSProperties = {
    height: '20px',
    borderRadius: '4px',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '20px',
    letterSpacing: '0.04px',
    verticalAlign: 'middle',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 6px',
    minWidth: '58px',
    textAlign: 'center',
  };

  switch (status) {
    case 'SCANNED':
      return { ...baseStyle, background: '#DEF0FF', color: '#003FD2' };
    case 'SIGNEDOFF':
      return { ...baseStyle, background: '#DAF3EE', color: '#00795C' };
    case 'DRAFTED':
      return { ...baseStyle, background: '#F5EFE2', color: '#8A6A2E' };
    case 'REVIEWED':
      // Non-final, re-editable like DRAFTED — own indigo tint.
      return { ...baseStyle, background: '#EAE7FB', color: '#5436B8' };
    case 'NOT_FOR_REPORTING':
      return { ...baseStyle, background: '#FCE3E3', color: '#A8071A' };
    case 'CANCELLED':
      return { ...baseStyle, background: '#F1F1F4', color: '#6B7280' };
    default:
      return { ...baseStyle, background: '#DEF0FF', color: '#003FD2' };
  }
};

/**
 * Status Cell Renderer
 * Displays status as a colored tag with re-reported indicator
 */
export const StatusCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const status = params.data?.order_workflow?.ow_pacs_status || '';
  const isReReported = record?.order_workflow?.ow_re_reported;
  const onViewReport = params.context?.onViewReport;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (status === 'SIGNEDOFF' && onViewReport) {
      onViewReport(record);
    }
  };

  if (!status) return <span>-</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Tag
        style={{
          ...getStatusStyle(status),
          cursor: status === 'SIGNEDOFF' ? 'pointer' : 'default',
        }}
        onClick={handleClick}
      >
        {getPacsStatusLabel(status)}
      </Tag>
      {isReReported && <ReReportedBadge />}
    </div>
  );
};

/**
 * Modality Cell Renderer
 * Displays modality in a styled badge
 */
export const ModalityCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const modality = params.data?.pacs_order?.po_modality || '';

  if (!modality) return <span>-</span>;

  return (
    <span
      title={modality}
      style={{
        background: '#F5F0FA',
        border: '1px solid #E0D8EA',
        height: '24px',
        borderRadius: '4px',
        padding: '2px 8px',
        display: 'inline-block',
        lineHeight: '20px',
        fontSize: '13px',
        fontWeight: 500,
        color: '#4a4a4a',
        // Grid-wide wrap CSS (word-break: break-word) splits multi-modality
        // csv values like "CT,PT" (T002 item groups) at the comma — keep the
        // badge on one line; anything longer clips with the title tooltip.
        whiteSpace: 'nowrap',
      }}
    >
      {modality}
    </span>
  );
};

/**
 * Patient Name Cell Renderer
 * Clickable button that opens report editor, with emergency indicator
 * Accesses callbacks from AG Grid context (passed via context prop)
 */
export const PatientNameCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const patientName = record?.pacs_order?.patient?.pat_name || '';
  // Emergency cases are now signalled by a reddish row tint (see getRowClass),
  // not an inline icon, so emergency no longer affects this cell's layout.
  const isMlc = record?.pacs_order?.po_is_mlc === true;
  const currentlyBlocked = record?.order_workflow?.ow_reporting_blocked === true;
  const studyUID = record?.ps_study_uid;

  // Access callbacks from AG Grid context
  const onOpenReport = params.context?.onOpenReport;
  const onOpenViewer = params.context?.onOpenViewer;
  const onOpenRadiant = params.context?.onOpenRadiant;
  const enableRadiantViewer = params.context?.enableRadiantViewer;
  const onShowPatientOrders = params.context?.onShowPatientOrders;

  // Technicians don't report — for them the patient-name click opens the viewer
  // instead of the report editor (T009). Everyone else opens reporting as before.
  const isTechnician = getUserDetails()?.user_type === 'technician';

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTechnician) {
      if (onOpenViewer) onOpenViewer(record);
      return;
    }
    if (!currentlyBlocked && onOpenReport) {
      onOpenReport(record);
    }
  };

  const handleRadiantClick = (e: any) => {
    e.domEvent?.stopPropagation();
    if (studyUID && onOpenRadiant) {
      onOpenRadiant(studyUID);
    }
  };

  const nameButton = (
    <Button
      type="link"
      onClick={handleClick}
      style={{
        padding: 0,
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        maxWidth: '100%',
        flex: 1,
        minWidth: 0,
      }}
      disabled={!isTechnician && currentlyBlocked}
    >
      <Tooltip title={patientName}>
        <span
          style={{
            ...TEXT_CLAMP_2,
            maxWidth: isMlc ? 'calc(100% - 34px)' : '100%',
            color: (!isTechnician && currentlyBlocked) ? '#b0b0b0' : '#2563EB',
          }}
        >
          {patientName}
        </span>
      </Tooltip>
      {isMlc && (
        <Tooltip title={getMlcTooltip(record?.pacs_order)}>
          <span
            style={{
              marginLeft: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              height: '15px',
              padding: '0 2px',
              fontSize: '9px',
              fontWeight: 700,
              lineHeight: '15px',
              letterSpacing: '0',
              color: '#fff',
              background: '#475569',
              borderRadius: '3px',
            }}
          >
            M
          </span>
        </Tooltip>
      )}
    </Button>
  );
  const ordersButton = (onShowPatientOrders && record?.has_prior_orders) ? (
    <Tooltip title="View Prior Orders">
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onShowPatientOrders(record);
        }}
        style={{
          flexShrink: 0,
          background: '#f5eeff',
          borderRadius: '4px',
        }}
      >
        <RightCircleOutlined/>
      </Button>
    </Tooltip>
  ) : null;

  const fullContent = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', overflow: 'hidden' }}>
      {nameButton}
      {ordersButton}
    </div>
  );

  // Right-click context menu: always offer "Copy Study UID"; the Radiant Viewer
  // entry is added only when Radiant is enabled. fullContent is a full-width div,
  // so the entire cell is the trigger.
  const contextMenu = (
    <Menu>
      <Menu.Item
        key="copy-study-uid"
        disabled={!studyUID}
        onClick={(e) => {
          e.domEvent.stopPropagation();
          if (!studyUID) {
            return;
          }
          navigator.clipboard
            .writeText(studyUID)
            .then(() => message.success('Study UID copied'))
            .catch(() => message.error('Failed to copy Study UID'));
        }}
      >
        Copy Study UID
      </Menu.Item>
      {enableRadiantViewer && onOpenRadiant && studyUID && (
        <Menu.Item key="radiant" onClick={handleRadiantClick}>
          Open in Radiant Viewer
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={contextMenu} trigger={['contextMenu']}>
      {fullContent}
    </Dropdown>
  );
};

/**
 * Tooltip Text Cell Renderer
 * Wraps text content with ellipsis and tooltip
 */
interface TooltipTextRendererProps extends ICellRendererParams {
  valueGetter?: (data: any) => string;
}

export const TooltipTextCellRenderer: React.FC<TooltipTextRendererProps> = (params) => {
  let value = params.value;

  // If custom valueGetter provided, use it
  if (params.valueGetter) {
    value = params.valueGetter(params.data);
  }

  if (!value) return <span style={{ color: '#b0b0b0', textAlign: 'center', width: '100%', display: 'block' }}>-</span>;

  return (
    <Tooltip title={value}>
      <div
        style={TEXT_CLAMP_2}
      >
        {value}
      </div>
    </Tooltip>
  );
};

/**
 * Patient ID Cell Renderer
 */
export const PatientIdCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const patId = params.data?.pacs_order?.patient?.pat_pin || '';

  if (!patId) return <span>-</span>;

  return (
    <Tooltip title={patId}>
      <div
        style={TEXT_CLAMP_2}
      >
        {patId}
      </div>
    </Tooltip>
  );
};

/**
 * Body Part Cell Renderer
 */
export const BodyPartCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const bodyPart = params.data?.pacs_order?.po_body_part || '';

  if (!bodyPart) return <span>-</span>;

  return (
    <Tooltip title={bodyPart}>
      <div
        style={TEXT_CLAMP_2}
      >
        {bodyPart}
      </div>
    </Tooltip>
  );
};

/**
 * Study Description Cell Renderer
 */
export const StudyDescCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const studyDesc = params.data?.pacs_order?.po_diag_desc || '';
  const priority = params.data?.pacs_order?.po_priority || '';

  if (!studyDesc) return <span>-</span>;

  const tooltipText = priority ? `${studyDesc} (Priority: ${priority})` : studyDesc;

  return (
    <Tooltip title={tooltipText}>
      <div
        style={TEXT_CLAMP_2}
      >
        {studyDesc}
      </div>
    </Tooltip>
  );
};

/**
 * Series/Image Count Cell Renderer
 */
export const SeriesCountCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const seriesCount = params.data?.ps_series_count || 0;
  const imageCount = params.data?.ps_image_count || 0;
  const isIngesting = !params.data?.is_study_ingestion_complete;

  const tooltipText = isIngesting
    ? 'Images are still being received from modality'
    : `Series: ${seriesCount}, Images: ${imageCount}`;

  return (
    <Tooltip title={tooltipText}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 6,
          width: '100%',
        }}
      >
        <span>
          {seriesCount} / {imageCount}
        </span>
      </div>
    </Tooltip>
  );
};

/**
 * Accession Number Cell Renderer
 */
export const AccessionNumberCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const accNo = params.data?.pacs_order?.po_acc_no || '';

  if (!accNo) return <span>-</span>;

  return (
    <Tooltip title={accNo}>
      <div
        style={TEXT_CLAMP_2}
      >
        {accNo}
      </div>
    </Tooltip>
  );
};

/**
 * Referring Doctor Cell Renderer
 */
export const RefDoctorCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const refDoc = params.data?.pacs_order?.po_ref_doc || '';

  return (
    <Tooltip title={refDoc || 'No referring doctor'}>
      <div
        style={{
          ...TEXT_CLAMP_2,
          textAlign: refDoc ? 'left' : 'center',
          color: refDoc ? '#4a4a4a' : '#b0b0b0',
        }}
      >
        {refDoc || '-'}
      </div>
    </Tooltip>
  );
};

/**
 * Date/Time Cell Renderer
 */
export const DateTimeCellRenderer: React.FC<ICellRendererParams> = (params) => {
  return <StudyDateTime value={params.value} />;
};

/**
 * Reported By Cell Renderer
 */
export const ReportedByCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const userName = params.data?.reportedBy || '';

  if (!userName) return <span>-</span>;

  return (
    <Tooltip title={userName}>
      <div
        style={TEXT_CLAMP_2}
      >
        {userName}
      </div>
    </Tooltip>
  );
};

/**
 * Signed By Cell Renderer
 */
export const SignedByCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const userName = params.data?.signedBy || '';

  if (!userName) return <span>-</span>;

  return (
    <Tooltip title={userName}>
      <div
        style={TEXT_CLAMP_2}
      >
        {userName}
      </div>
    </Tooltip>
  );
};

/**
 * Assigned Radiologist Cell Renderer
 * Displays a dropdown for users with ASSIGN_CASES permission, plain text otherwise
 * Accesses userList and onRadiologistAssignment from AG Grid context
 */
export const AssignedRadiologistCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const assignedRadId = record?.order_workflow?.ow_assigned_rad_id;
  // const assignedRadName = record?.order_workflow?.ow_assigned_rad_name;
  // Radiologists never get the assignment dropdown, even if granted ASSIGN_CASES —
  // it is reserved for HOD/coordinators.
  const isRadiologist = getUserDetails()?.user_type === 'radiologist';
  const canAssign = hasPermission(Permissions.ASSIGN_CASES) && !isRadiologist;

  // Access data from AG Grid context
  const userList = params.context?.userList || [];
  const onRadiologistAssignment = params.context?.onRadiologistAssignment;
  const pacs_status = record?.order_workflow?.ow_pacs_status;
  const isSignedOff = pacs_status === 'SIGNEDOFF';
  if (canAssign) {
    // Filter to only radiologists and map to Select options
    const radiologistOptions = userList
      ?.filter((u: any) => u.user_type === 'radiologist')
      .map((user: any) => ({
        label: `${user.user_fullname} (${user.username})`,
        value: user.username,
      })) || [];

  // For SIGNEDOFF status, show Select disabled
      return (
        <Select
          value={assignedRadId || undefined}
          placeholder="Unassigned"
          allowClear={!isSignedOff}
          disabled={isSignedOff}
          showSearch={!isSignedOff}
          options={radiologistOptions}
          optionFilterProp="label"
          onChange={(value) => {
            if (onRadiologistAssignment) {
              onRadiologistAssignment(record, value, params.rowIndex);
            }
          }}
          style={{
            width: '100%',
            minWidth: '150px',
          }}
        />
      );
  }

  // Show plain text for users without permission
  return (
    <Tooltip title={assignedRadId || 'Unassigned'}>
      <div
        style={{
          ...TEXT_CLAMP_2,
          color: assignedRadId ? '#4a4a4a' : '#b0b0b0',
        }}
      >
        {assignedRadId || '-'}
      </div>
    </Tooltip>
  );
};

/**
 * Assigned Technician Cell Renderer
 * Shows a Select dropdown matching the Orders page technician column.
 * Current user is pinned at the top as "(Self Assign)"; all other technicians
 * follow. Calls onAssignTechnician(record, username, rowIndex) on change.
 */
export const AssignedTechnicianCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const assignedTechnician = record?.order_workflow?.ow_assigned_technician;

  const onAssignTechnician = params.context?.onAssignTechnician;
  const userList: any[] = params.context?.userList || [];
  const currentUser = params.context?.currentUser;
  const currentUsername = currentUser?.username;

  const technicianOptions = [
    ...(currentUsername
      ? [{ label: `${currentUser?.user_fullname || currentUsername} (Self Assign)`, value: currentUsername }]
      : []),
    ...userList
      .filter(u => u?.user_type === 'technician' && u?.username !== currentUsername)
      .map(u => ({ label: u.user_fullname || u.username, value: u.username })),
  ];

  return (
    <Select
      value={assignedTechnician || undefined}
      placeholder="Assign technician"
      size="small"
      style={{ width: '100%', minWidth: 150 }}
      showSearch
      filterOption={(input, option) =>
        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
      }
      options={technicianOptions}
      onChange={value => {
        if (onAssignTechnician) onAssignTechnician(record, value, params.rowIndex);
      }}
      onClick={e => e.stopPropagation()}
    />
  );
};
